import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {TranslocoDirective, TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Card} from 'primeng/card';
import {MultiTabStepBaseComponent} from '../../../../../../shared/tabs/multi-tab-step-base.component';
import {TabStatus} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {
  ProjectChapterType,
  ProjectStepType,
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {
  MAIL_PREVIEW_FIELD_MAP,
  MAIL_PREVIEW_SECTIONS,
  Step93RfdMailPreviewComponent,
} from './step-7-3-rfd-invitation-mail-preview/step-7-3-rfd-invitation-mail-preview.component';
import {
  PROJECT_DESCRIPTION_FIELD_MAP,
  PROJECT_DESCRIPTION_SECTIONS,
  Step73RfdProjectDescriptionComponent,
} from './step-7-3-rfd-project-description/step-7-3-rfd-project-description.component';
import {Step73RfdAgendaSummaryComponent} from './step-7-3-rfd-agenda-summary/step-7-3-rfd-agenda-summary.component';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {
  ProjectStepRichTextService,
  calculateRichTextSectionStatus,
} from '../../../../../../shared/services/project-step-rich-text.service';
import {createTabStatusSignal} from '../../../../../../shared/tabs/tab-utils';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-step-9-3-invitation-preview',
  providers: [ProjectStepRichTextService],
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    Tab,
    TabList,
    Tabs,
    TabPanels,
    TabPanel,
    Card,
    Step93RfdMailPreviewComponent,
    Step73RfdProjectDescriptionComponent,
    Step73RfdAgendaSummaryComponent,
    PreviousStepIncompleteNoticeComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-7-3-invitation-preview.component.html',
})
export class Step73InvitationPreviewComponent extends MultiTabStepBaseComponent implements OnInit {
  private readonly tenderInvitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );

  protected readonly mailPreviewTabStatus = createTabStatusSignal();

  protected readonly projectDescriptionTabStatus = createTabStatusSignal();

  protected readonly summaryTabStatus = createTabStatusSignal();

  invitationPreviewResponse = signal<TenderInvitationPreviewResponse | undefined>(undefined);

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_DEMONSTRATION_USE_CASES},
    ]);
  });

  constructor() {
    super();
    effect(() => {
      if (
        this.projectId &&
        this.isStepPrerequisiteCompleted() &&
        !this.invitationPreviewResponse()
      ) {
        this.loadAllTabData();
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  private loadAllTabData(): void {
    this.tenderInvitationPreviewControllerService
      .getTenderInvitationPreview({
        projectId: this.projectId,
        phase: TenderPhaseType.RFD,
      })
      .subscribe({
        next: (data: TenderInvitationPreviewResponse) => {
          this.invitationPreviewResponse.set(data);
          this.calculateInitialTabStatuses();
        },
      });
  }

  private calculateInitialTabStatuses(): void {
    const invitationData = this.invitationPreviewResponse();

    if (invitationData) {
      const mailPreviewCompleted = calculateRichTextSectionStatus(
        invitationData,
        MAIL_PREVIEW_SECTIONS,
        MAIL_PREVIEW_FIELD_MAP,
      );

      const mailPreviewStatus = {
        totalSections: MAIL_PREVIEW_SECTIONS.length,
        completedSections: mailPreviewCompleted,
      };
      this.onMailPreviewStatusChange(mailPreviewStatus);

      const projectDescriptionCompleted = calculateRichTextSectionStatus(
        invitationData,
        PROJECT_DESCRIPTION_SECTIONS,
        PROJECT_DESCRIPTION_FIELD_MAP,
      );

      const projectDescriptionStatus = {
        totalSections: PROJECT_DESCRIPTION_SECTIONS.length,
        completedSections: projectDescriptionCompleted,
      };
      this.onProjectDescriptionStatusChange(projectDescriptionStatus);

      // Mark the summary tab always as completed since it's read-only
      this.summaryTabStatus.update({
        totalSections: 1,
        completedSections: 1,
      });
    }
  }

  onMailPreviewStatusChange(status: TabStatus): void {
    this.mailPreviewTabStatus.update(status);
  }

  onProjectDescriptionStatusChange(status: TabStatus): void {
    this.projectDescriptionTabStatus.update(status);
  }
}
